import { useEffect, useState } from "react"
import API from "../services/api"
import "./RestaurantBarClient.css"

// Translation system
const translations = {
  fr: {
    // Header
    back: "Retour",

    // Welcome banner
    restaurantBar: "Nourriture et boissons",
    exploreOurServices: "Explorez nos services de restaurant et bar",

    // Cartes navigation
    restaurant: "Restaurant",
    bar: "Bar",
    petitDejeuner: "Petit déjeuner",
    roomService: "En-cas",

    // Footer
    contact: "Contact",
    address: "Adresse",
    reservations: "Réservations",
    wifi: "Wi-Fi",
    followUs: "Suivez-nous",
    network: "Réseau",
    password: "Mot de passe",
    availableAtReception: "Réseau Accor",
    addressLine1: "Rue de Syrie",
    addressLine2: "1002 TUNIS",
    allRightsReserved: "Tous droits réservés",
    createdBy: "Créé par",
  },
  en: {
    // Header
    back: "Back",

    // Welcome banner
    restaurantBar: "Food and Drinks",
    exploreOurServices: "Explore our restaurant and bar services",

    restaurant: "Restaurant",
    bar: "Bar",
    petitDejeuner: "Breakfast",
    roomService: "Snacks",

    // Footer
    contact: "Contact",
    address: "Address",
    reservations: "Reservations",
    wifi: "Wi-Fi",
    followUs: "Follow Us",
    network: "Network",
    password: "Password",
    availableAtReception: "accor network",
    addressLine1: "Rue de Syrie",
    addressLine2: "1002 TUNIS",
    allRightsReserved: "All rights reserved",
    createdBy: "Created by",
  },
  ar: {
    // Header
    back: "رجوع",

    // Welcome banner
    restaurantBar: "المطعم والبار",
    exploreOurServices: "استكشف خدمات المطعم والبار لدينا",

    restaurant: "المطعم",
    bar: "البار",
    petitDejeuner: "الإفطار",
    roomService: "وجبات خفيفة",

    // Footer
    contact: "اتصل بنا",
    address: "العنوان",
    reservations: "الحجوزات",
    wifi: "واي فاي",
    followUs: "تابعونا",
    network: "الشبكة",
    password: "كلمة المرور",
    availableAtReception: "شبكة أكور",
    addressLine1: "شارع الكورنيش",
    addressLine2: "تونس، 3000 تونس",
    allRightsReserved: "جميع الحقوق محفوظة",
    createdBy: "تم إنشاؤه بواسطة",
  },
}

const languages = [
  { code: "fr", name: "Français", flag: "/images/fr-flag-v2.png" },
  { code: "en", name: "English", flag: "/images/en-flag-v2.png" },
  { code: "ar", name: "العربية", flag: "/images/ar-flag-v2.png" },
]

const RestaurantBarClient = () => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    if (typeof window === "undefined") return "fr"
    const saved = (localStorage.getItem("IBIS-language") || "").trim().toLowerCase()
    if (saved && translations[saved]) return saved
    return "fr"
  })
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false)
  const [pageContent, setPageContent] = useState(null)

  // Get translation function
  const t = (key) => translations[currentLanguage][key] || translations.fr[key] || key

  // Helpers: afficher le contenu selon la langue sélectionnée
  const getPageContentDescription = () => {
    if (!pageContent) return ""
    const tr = pageContent.translations || {}
    return tr[currentLanguage]?.description ?? tr.fr?.description ?? pageContent.description ?? ""
  }
  const getPageContentImage = () => {
    if (!pageContent) return null
    const tr = pageContent.translations || {}
    return (
      tr[currentLanguage]?.image ??
      tr.fr?.image ??
      pageContent.image ??
      pageContent.images?.[0] ??
      null
    )
  }

  const getPageContentVideo = () => {
    if (!pageContent) return null
    const tr = pageContent.translations || {}
    return tr[currentLanguage]?.video ?? tr.fr?.video ?? pageContent.video ?? null
  }

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const res = await API.get("/page-contents/page/RestaurantBar") // 👈 pageName = RestaurantBar
        setPageContent(res.data)
      } catch (err) {
        console.error("Error fetching RestaurantBar page content:", err)
        setPageContent(null)
      }
    }
    fetchPageContent()
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)

    const raw = localStorage.getItem("IBIS-language")
    const savedLanguage = raw ? raw.trim().toLowerCase() : null
    if (savedLanguage && translations[savedLanguage]) {
      setCurrentLanguage(savedLanguage)
      document.documentElement.dir = savedLanguage === "ar" ? "rtl" : "ltr"
      document.documentElement.lang = savedLanguage
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const raw2 = localStorage.getItem("IBIS-language")
        const lang = raw2 ? raw2.trim().toLowerCase() : null
        if (lang && translations[lang]) {
          setCurrentLanguage(lang)
          document.documentElement.dir = lang === "ar" ? "rtl" : "ltr"
          document.documentElement.lang = lang
        }
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange)
  }, [])

  // Language change handler
  const changeLanguage = (langCode) => {
    setCurrentLanguage(langCode)
    localStorage.setItem("IBIS-language", langCode)
    setShowLanguageDropdown(false)

    // Update document direction for Arabic
    document.documentElement.dir = langCode === "ar" ? "rtl" : "ltr"
    document.documentElement.lang = langCode
  }

  const getCurrentLanguage = () => languages.find((lang) => lang.code === currentLanguage)

  const foodNavCards = [
    {
      id: "restaurant",
      titleKey: "restaurant",
      path: "/RestaurantsMenus-client",
      image: "/images/restarant.jpg",
      wide: true,
    },
    {
      id: "petit-dejeuner",
      titleKey: "petitDejeuner",
      path: "/petitdejeuner-client",
      image: "/images/petitdejeuner.jpg",
      wide: true,
    },
    {
      id: "encas",
      titleKey: "roomService",
      path: "/encas-client",
      image: "/images/restaurant.jpg",
      wide: false,
    },
    {
      id: "bar",
      titleKey: "bar",
      path: "/boissons-client",
      image: "/images/bar.jpg",
      wide: false,
    },
  ]

  return (
    <div className={`hotel-app5 ${currentLanguage === "ar" ? "rtl" : "ltr"}`}>
      {/* Language Selector */}
      <div className="language-selector">
        <button className="language-toggle" onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}>
          <img
            src={getCurrentLanguage()?.flag || "/placeholder.svg"}
            alt={getCurrentLanguage()?.name}
            className="language-flag"
          />
          <span>{getCurrentLanguage()?.code.toUpperCase()}</span>
        </button>
        {showLanguageDropdown && (
          <div className="language-dropdown">
            {languages.map((lang) => (
              <div
                key={lang.code}
                className={`language-option ${currentLanguage === lang.code ? "active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation()
                  changeLanguage(lang.code)
                }}
              >
                <img src={lang.flag || "/placeholder.svg"} alt={lang.name} className="flag-small" />
                <span>{lang.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <header className="app-header">
        <button
          className="header-back-link"
          onClick={() => {
            window.location.href = "/home"
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d={currentLanguage === "ar" ? "M5 12H19M19 12L12 5M19 12L12 19" : "M19 12H5M5 12L12 19M5 12L12 5"}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {t("back")}
        </button>
        <div className="logo-container">
          <img src="/images/logo2.webp" alt="Ibis Logo" className="logo" />
        </div>
        <div></div>
      </header>

      <main className="app-main app-main--food-grid">
        {pageContent && (
          <div className="page-content-restaurant">
            {getPageContentVideo() && (
              <div className="page-content-video-wrap">
                <video
                  src={getPageContentVideo()}
                  controls
                  playsInline
                  preload="metadata"
                  className="page-content-video"
                />
              </div>
            )}
            {getPageContentImage() && (
              <img
                src={getPageContentImage()}
                alt="Restaurant & Bar"
                className="page-content-image"
                onError={(e) => (e.target.src = "/placeholder.svg")}
              />
            )}
            {getPageContentDescription() && (
              <div className="content-description">
                <div
                  className="page-content-html-light"
                  dangerouslySetInnerHTML={{ __html: getPageContentDescription() }}
                />
              </div>
            )}
          </div>
        )}

        <div className="welcome-banner welcome-banner--compact">
          <h1>
            <span>{t("restaurantBar")}</span>
          </h1>
          <p>{t("exploreOurServices")}</p>
        </div>

        <div className="rb-food-grid">
          {foodNavCards.map((card) => (
            <a
              key={card.id}
              href={card.path}
              className={`rb-food-card${card.wide ? " rb-food-card--wide" : ""}`}
              style={{ backgroundImage: `url("${card.image}")` }}
            >
              <span className="rb-food-card-overlay" aria-hidden={true} />
              <span className="rb-food-card-label">{t(card.titleKey)}</span>
            </a>
          ))}
        </div>
      </main>

      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>{t("contact")}</h4>
            <p>+216 71 830 300</p>
            <p>H6144@accor.com</p>
          </div>
          <div className="footer-section">
            <h4>{t("address")}</h4>
            <p>{t("addressLine1")}</p>
            <p>{t("addressLine2")}</p>
          </div>
          <div className="footer-section">
            <h4>{t("reservations")}</h4>
            <p>+216 71 830 300</p>
            <p>H6144@accor.com</p>
          </div>
          <div className="footer-section">
            <h4>{t("wifi")}</h4>
            <p>{t("availableAtReception")}</p>
          </div>
          <div className="footer-section">
            <h4>{t("followUs")}</h4>
            <div className="social-links">
              <a href="https://www.facebook.com/ibisTunis" aria-label="Facebook">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                </svg>
              </a>
              <a href="https://www.instagram.com/ibistunis/" aria-label="Instagram">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                </svg>
              </a>
            </div>
          </div>
        </div>
        <div className="copyright">
          <p>
            © {new Date().getFullYear()} IBIS Tunis. {t("allRightsReserved")}.
            <br />
            {t("createdBy")}{" "}
            <a href="https://www.itbafa.com" target="_blank" rel="noopener noreferrer">
              <img
                src="/images/itbafa_logo_white.png"
                alt="ITBAFA Logo"
                style={{ height: "20px", verticalAlign: "middle", marginLeft: "5px" }}
              />
            </a>
          </p>
        </div>
      </footer>
    </div>
  )
}

export default RestaurantBarClient
